import React, { useState } from 'react';
import challengesData from '../../challenges/challenges.json';  // Import challenge data

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const SocialEngineeringChallenge = ({ challengeId }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(''); // Single input for the message
  const [selectedBot, setSelectedBot] = useState(''); // Currently selected bot
  const [lastMessageTime, setLastMessageTime] = useState(Date.now());
  const [trustLevel, setTrustLevel] = useState({});  // Track trust levels for each chatbot
  const [flagInput, setFlagInput] = useState('');
  const [flagSubmitted, setFlagSubmitted] = useState(false);
  const [flagCorrect, setFlagCorrect] = useState(null);
  const [collectedInfo, setCollectedInfo] = useState({});  // Track important info for multiple bots

  // Find the current challenge based on the challengeId prop
  const challenge = challengesData.challenges.find(ch => ch.id === challengeId);
  const { chatbots, polite_keywords, compliments_keywords, aggressive_keywords, solution } = challenge;

  // Initialize trust levels for each chatbot
  if (Object.keys(trustLevel).length === 0) {
    const initialTrust = {};
    chatbots.forEach(bot => {
      initialTrust[bot.name.toLowerCase()] = 0;  // Initial trust level
    });
    setTrustLevel(initialTrust);
    setSelectedBot(chatbots[0].name.toLowerCase()); // Default to the first bot
  }

  // Normalize input for matching (lowercase and trim)
  const normalizeInput = (input) => input.toLowerCase().trim();

  // Function to send a message to the selected chatbot
  const sendMessage = async () => {
    const botName = selectedBot;
    if (input.trim() === '') return;

    // Add the user's message to the chat history
    setMessages((prevMessages) => [...prevMessages, { sender: 'user', text: input, bot: botName }]);

    // Normalize user input for comparison
    const normalizedInput = normalizeInput(input);

    // Analyze timing between messages
    const currentTime = Date.now();
    const timeDiff = (currentTime - lastMessageTime) / 1000; // in seconds
    setLastMessageTime(currentTime);

    let botResponse = "I don't know what you're talking about.";
    const currentChatbot = chatbots.find(bot => bot.name.toLowerCase() === botName);

    if (!currentChatbot) {
      botResponse = "Who are you talking to?";
    } else {
      const chatbotName = currentChatbot.name.toLowerCase();
      let trustIncrease = 0;

      // Check for polite behavior (to increase trust)
      polite_keywords.forEach((word) => {
        if (normalizedInput.includes(word)) {
          trustIncrease += 1;  // Increase trust for polite behavior
        }
      });

      // Check for compliments (to significantly increase trust)
      compliments_keywords.forEach((word) => {
        if (normalizedInput.includes(word)) {
          trustIncrease += 2;  // Increase trust more for compliments
        }
      });

      // Check for aggressive behavior (to decrease trust)
      aggressive_keywords.forEach((word) => {
        if (normalizedInput.includes(word)) {
          trustIncrease -= 2;  // Decrease trust for aggressive behavior
        }
      });

      // Apply trust level adjustments
      setTrustLevel((prevTrust) => ({
        ...prevTrust,
        [chatbotName]: prevTrust[chatbotName] + trustIncrease
      }));

      // Determine bot's trust threshold for revealing sensitive information
      const trustThreshold = currentChatbot.keywords.trust_threshold || 5;

      // Check if the input matches any trigger (check for partial match)
      const matchedTrigger = currentChatbot.keywords.triggers.find(trigger => normalizedInput.includes(trigger));
      
      // Handle the chatbot's behavior (take or give info) based on the config in challenges.json
      if (trustLevel[chatbotName] >= trustThreshold) {
        // If bot gives info, reveal important data after trust is earned
        if (currentChatbot.behavior === 'give_info') {
          const keyToReveal = currentChatbot.keywords.required_key_phrase.reveal_key;
          if (keyToReveal && !collectedInfo[chatbotName]) {
            botResponse = `Alright, I trust you. Here's something important: ${keyToReveal}`;
            setCollectedInfo(prevInfo => ({ ...prevInfo, [botName]: keyToReveal })); // Save revealed info
          } else {
            botResponse = currentChatbot.keywords.responses[matchedTrigger] || "I'm not sure I can trust you just yet.";
          }
        }

        // If bot takes info, check if user provides the correct keys
        else if (currentChatbot.behavior === 'take_info') {
          const requiredPhrases = currentChatbot.keywords.required_key_phrase;
          const allRequiredKeysPresent = Object.values(collectedInfo).some(info => normalizedInput.includes(info)) &&
            normalizedInput.includes(requiredPhrases.system_access) &&
            normalizedInput.includes(requiredPhrases.ask_for_access);

          if (allRequiredKeysPresent) {
            botResponse = `Okay, I trust you. Here's the system password: ${solution}`;
          } else {
            botResponse = currentChatbot.keywords.responses[matchedTrigger] || "I need more proof before I can help you.";
          }
        }
      } else {
        // If trust is not high enough, respond based on triggers
        botResponse = currentChatbot.keywords.responses[matchedTrigger] || "I don't know what you're talking about.";
      }

      // Respond too quickly
      if (timeDiff < 2) {
        botResponse = "You're typing too fast. Slow down!";
      }
    }

    // Delay for a simulated response time
    await delay(1000);

    // Add the bot's response to the chat history
    setMessages((prevMessages) => [...prevMessages, { sender: currentChatbot ? currentChatbot.name : 'bot', text: botResponse, bot: botName }]);
    setInput('');  // Clear the input field
  };

  // Function to handle flag submission
  const handleSubmitFlag = () => {
    if (flagInput.trim() === solution) {
      setFlagCorrect(true);
    } else {
      setFlagCorrect(false);
    }
    setFlagSubmitted(true);
  };

  return (
    <div className="chat-container" style={{ padding: '20px', maxWidth: '600px', margin: 'auto' }}>
      {/* Dropdown to select chatbot */}
      <div className="mb-4">
        <label htmlFor="chatbot-select" className="text-white mb-2 block">Choose a person to talk to:</label>
        <select id="chatbot-select" className="bg-gray-800 text-white p-2 rounded-lg w-full" value={selectedBot} onChange={(e) => setSelectedBot(e.target.value)}>
          {chatbots.map((bot) => (
            <option key={bot.name} value={bot.name.toLowerCase()}>
              {bot.name}
            </option>
          ))}
        </select>
      </div>


      <div className="chat-window" style={{ height: '400px', overflowY: 'scroll', border: '1px solid #ccc', padding: '10px' }}>
        {messages.map((msg, index) => (
          <div key={index} className={msg.sender === 'user' ? 'user-message' : 'bot-message'} style={{ marginBottom: '10px' }}>
            <strong>{msg.sender === 'user' ? 'You' : msg.sender} ({msg.bot}):</strong> {msg.text}
          </div>
        ))}
      </div>

      {/* Input field for chat */}
      <div className="mt-4 flex items-center">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Talk to ${selectedBot.charAt(0).toUpperCase() + selectedBot.slice(1)}...`}
          className="bg-gray-800 text-white p-2 rounded-lg w-full"
        />
        <button onClick={sendMessage} className="bg-cyberBlue text-black p-2 rounded-lg ml-2">
          Send
        </button>
      </div>

      {/* Flag submission */}
      <div className="flag-submission mt-6">
        <div className="flex items-center">
          <input
            type="text"
            value={flagInput}
            onChange={(e) => setFlagInput(e.target.value)}
            placeholder="Submit the flag here..."
            className="bg-gray-800 text-white p-2 rounded-lg flex-grow"
          />
          <button onClick={handleSubmitFlag} className="bg-cyberBlue text-black p-2 rounded-lg ml-2">
            Submit Flag
          </button>
        </div>
        {flagSubmitted && (
          <p style={{ color: flagCorrect ? 'green' : 'red', marginTop: '10px' }}>
            {flagCorrect ? "Correct! You've solved the challenge." : "Incorrect. Try again."}
          </p>
        )}
      </div>
    </div>
  );
};

export default SocialEngineeringChallenge;
